import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define the new structure for the selected note type
interface SelectedNoteType {
    id: string;
    name: string;
}

interface ConsultationState {
    consultationId: string;
    section: string;
    category: string;
    patientId: string;
    patientName: string;
    securityPin: string;
    currentTime: string;
    isConsent: boolean;
    language: string;
    noteType: SelectedNoteType;
    notesLength: number;
    patientsList: any[];
    notes: any[];
    attachedFiles: any[];
    images: any[];
    templates: any[];
    savedNotes: any[];
    consultationsList: any[];
    recentNotes: any[];
    selectedConsultationNote: any;
    selectedAttachedFile: any;
    selectedImage: any;
    isNewNoteCreated: boolean;
    isNewFileAttached: boolean;
    isNewImageUploaded: boolean;
    isNewTemplateCreated: boolean;
    initialRendering: boolean;
    mouseInfo: any;
    selectPatientSearchQuery: string;
    isImageSlideOpened: boolean;
    isRefreshContent: boolean;
    isConsentPopupOpened: boolean;
    isLanguagePopupOpened: boolean;
    isNoteDeletionPopupOpened: boolean;
    isSettingsPopupOpened: boolean;
    isFileExplorerPopupOpened: boolean;
    isNoteViewerOpened: boolean;
    isNoteSettingsPopupOpened: boolean;
    isNoteEditingPopupOpened: boolean;
    isNoteExportPopupOpened: boolean;
    isQrCodePopupOpened: boolean;
    isRefreshFilesList: boolean;
    isRefreshConsultationsList: boolean;
    isRefreshRecentNotesList: boolean;
    isRefreshTabs: boolean;
    isConsultationsDropdownOpened: boolean;
    selectedConsultation: any;
    selectedTemplate: any;
    templateOptionsXPosition: number,
    templateOptionsYPosition: number,
    isNoteTypePopupOpened: boolean;
    isFaxPopupOpened: boolean; // FIX: Add state for the fax popup
}

const initialState: ConsultationState = {
    consultationId: '',
    section: 'selectPatient',
    category: 'actions',
    patientId: '',
    patientName: '',
    securityPin: '',
    currentTime: '',
    isConsent: false,
    language: 'en',
    // UPDATED: Default noteType is now an object
    noteType: { id: 'ClinicQuebec', name: 'Clinic (Quebec)' },
    notesLength: 0,
    patientsList: [],
    notes: [],
    attachedFiles: [],
    images: [],
    templates: [],
    savedNotes: [],
    consultationsList: [],
    recentNotes: [],
    selectedConsultationNote: null,
    selectedAttachedFile: null,
    selectedImage: null,
    isNewNoteCreated: false,
    isNewFileAttached: false,
    isNewImageUploaded: false,
    isNewTemplateCreated: false,
    initialRendering: true,
    mouseInfo: null,
    selectPatientSearchQuery: '',
    isImageSlideOpened: false,
    isRefreshContent: false,
    isConsentPopupOpened: false,
    isLanguagePopupOpened: false,
    isNoteDeletionPopupOpened: false,
    isNoteViewerOpened: false,
    isNoteSettingsPopupOpened: false,
    isSettingsPopupOpened: false,
    isNoteEditingPopupOpened: false,
    isNoteExportPopupOpened: false,
    isFileExplorerPopupOpened: false,
    isQrCodePopupOpened: false,
    isRefreshFilesList: false,
    isRefreshConsultationsList: false,
    isRefreshRecentNotesList: false,
    isRefreshTabs: false,
    isConsultationsDropdownOpened: false,
    selectedConsultation: null,
    selectedTemplate: null,
    templateOptionsXPosition: 0,
    templateOptionsYPosition: 0,
    isNoteTypePopupOpened: false,
    isFaxPopupOpened: false, // FIX: Initialize the new state
};

const consultationSlice = createSlice({
  name: 'consultation',
  initialState,
  reducers: {
    setConsultationId: (state, action: PayloadAction<string>) => {
      state.consultationId = action.payload;
    },
    setSection: (state, action: PayloadAction<string>) => {
      state.section = action.payload;
    },
    setCategory: (state, action: PayloadAction<string>) => {
      state.category = action.payload;
    },
    setPatientId: (state, action: PayloadAction<string>) => {
      state.patientId = action.payload;
    },
    setPatientName: (state, action: PayloadAction<string>) => {
      state.patientName = action.payload;
    },
    setSecurityPin: (state, action: PayloadAction<string>) => {
      state.securityPin = action.payload;
    },
    setCurrentTime: (state, action: PayloadAction<string>) => {
      state.currentTime = action.payload;
    },
    setIsConsent: (state, action: PayloadAction<boolean>) => {
      state.isConsent = action.payload;
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload;
    },
    // UPDATED: Reducer now accepts the SelectedNoteType object
    setNoteType: (state, action: PayloadAction<SelectedNoteType>) => {
      state.noteType = action.payload;
    },
    setNotesLength: (state, action: PayloadAction<number>) => {
      state.notesLength = action.payload;
    },
    setPatientsList: (state, action: PayloadAction<any>) => {
      state.patientsList = action.payload;
    },
    setNotes: (state, action: PayloadAction<any>) => {
      state.notes = action.payload;
    },
    setAttachedFiles: (state, action: PayloadAction<any>) => {
      state.attachedFiles = action.payload;
    },
    setImages: (state, action: PayloadAction<any>) => {
      state.images = action.payload;
    },
    setTemplates: (state, action: PayloadAction<any>) => {
      state.templates = action.payload;
    },
    setSavedNotes: (state, action: PayloadAction<any>) => {
      state.savedNotes = action.payload;
    },
    setConsultationsList: (state, action: PayloadAction<any>) => {
      state.consultationsList = action.payload;
    },
    setRecentNotes: (state, action: PayloadAction<any>) => {
      state.recentNotes = action.payload;
    },
    setSelectedConsultationNote: (state, action: PayloadAction<any>) => {
      state.selectedConsultationNote = action.payload;
    },
    setSelectedAttachedFile: (state, action: PayloadAction<any>) => {
      state.selectedAttachedFile = action.payload;
    },
    setSelectedImage: (state, action: PayloadAction<any>) => {
      state.selectedImage = action.payload;
    },
    setIsNewNoteCreated: (state, action: PayloadAction<boolean>) => {
      state.isNewNoteCreated = action.payload;
    },
    setIsNewFileAttached: (state, action: PayloadAction<boolean>) => {
      state.isNewFileAttached = action.payload;
    },
    setIsNewImageUploaded: (state, action: PayloadAction<boolean>) => {
      state.isNewImageUploaded = action.payload;
    },
    setIsNewTemplateCreated: (state, action: PayloadAction<boolean>) => {
      state.isNewTemplateCreated = action.payload;
    },
    setInitialRendering: (state, action: PayloadAction<boolean>) => {
      state.initialRendering = action.payload;
    },
    setMouseInfo: (state, action: PayloadAction<any>) => {
      state.mouseInfo = action.payload
    },
    setSelectPatientSearchQuery: (state, action: PayloadAction<string>) => {
      state.selectPatientSearchQuery = action.payload
    },
    setIsImageSlideOpened: (state, action: PayloadAction<boolean>) => {
      state.isImageSlideOpened = action.payload
    },
    setIsRefreshContent: (state, action: PayloadAction<boolean>) => {
      state.isRefreshContent = action.payload
    },
    setIsConsentPopupOpened: (state, action: PayloadAction<boolean>) => {
      state.isConsentPopupOpened = action.payload
    },
    setIsLanguagePopupOpened: (state, action: PayloadAction<boolean>) => {
      state.isLanguagePopupOpened = action.payload
    },
    setIsNoteDeletionPopupOpened: (state, action: PayloadAction<boolean>) => {
      state.isNoteDeletionPopupOpened = action.payload
    },
    setIsNoteViewerOpened: (state, action: PayloadAction<boolean>) => {
      state.isNoteViewerOpened = action.payload
    },
    setIsNoteSettingsPopupOpened: (state, action: PayloadAction<boolean>) => {
      state.isNoteSettingsPopupOpened = action.payload
    },
    setIsSettingsPopupOpened: (state, action: PayloadAction<boolean>) => {
      state.isSettingsPopupOpened = action.payload
    },
    setIsNoteEditingPopupOpened: (state, action: PayloadAction<boolean>) => {
      state.isNoteEditingPopupOpened = action.payload
    },
    setIsNoteExportPopupOpened: (state, action: PayloadAction<boolean>) => {
      state.isNoteExportPopupOpened = action.payload
    },
    setIsFileExplorerPopupOpened: (state, action: PayloadAction<boolean>) => {
      state.isFileExplorerPopupOpened = action.payload
    },
    setIsQrCodePopupOpened: (state, action: PayloadAction<boolean>) => {
      state.isQrCodePopupOpened = action.payload
    },
    setIsRefreshFilesList: (state, action: PayloadAction<boolean>) => {
      state.isRefreshFilesList = action.payload
    },
    setIsRefreshConsultationsList: (state, action: PayloadAction<boolean>) => {
      state.isRefreshConsultationsList = action.payload
    },
    setIsRefreshRecentNotesList: (state, action: PayloadAction<boolean>) => {
      state.isRefreshRecentNotesList = action.payload
    },
    setIsRefreshTabs: (state, action: PayloadAction<boolean>) => {
      state.isRefreshTabs = action.payload
    },
    setIsConsultationsDropdownOpened: (state, action: PayloadAction<boolean>) => {
      state.isConsultationsDropdownOpened = action.payload
    },
    setSelectedConsultation: (state, action: PayloadAction<any>) => {
      state.selectedConsultation = action.payload
    },
    setSelectedTemplate: (state, action: PayloadAction<any>) => {
      state.selectedTemplate = action.payload
    },
    setTemplateOptionsXPosition: (state, action: PayloadAction<number>) => {
      state.templateOptionsXPosition = action.payload
    },
    setTemplateOptionsYPosition: (state, action: PayloadAction<number>) => {
      state.templateOptionsYPosition = action.payload
    },
    setIsNoteTypePopupOpened: (state, action: PayloadAction<boolean>) => {
        state.isNoteTypePopupOpened = action.payload;
    },
    setIsFaxPopupOpened: (state, action: PayloadAction<boolean>) => { // FIX: Add the new reducer
        state.isFaxPopupOpened = action.payload;
    },
    resetConsultation: (state) => {
      state.consultationId = '';
      state.section = 'selectPatient';
      state.category = 'actions';
      state.patientId = '';
      state.patientName = '';
      state.securityPin = '';
      state.currentTime = '';
      state.isConsent = false;
      state.language = 'en';
      // UPDATED: Reset note type to the default object
      state.noteType = { id: 'ClinicQuebec', name: 'Clinic (Quebec)' };
      state.notesLength = 0;
      state.patientsList = [];
      state.notes = [];
      state.selectedConsultationNote = null;
      state.selectedAttachedFile = null;
      state.selectedImage = null;
      state.attachedFiles = [];
      state.images = [];
      state.templates = [];
      state.savedNotes = [];
      state.consultationsList = [];
      state.recentNotes = [];
      state.isNewNoteCreated = false;
      state.isNewFileAttached = false;
      state.isNewImageUploaded = false;
      state.isNewTemplateCreated = false;
      state.initialRendering = true;
      state.mouseInfo = null;
      state.selectPatientSearchQuery = '';
      state.isImageSlideOpened = false;
      state.isRefreshContent = false;
      state.isNoteDeletionPopupOpened = false;
      state.isNoteViewerOpened = false;
      state.isNoteSettingsPopupOpened = false;
      state.isConsentPopupOpened = false;
      state.isLanguagePopupOpened = false;
      state.isSettingsPopupOpened = false;
      state.isNoteEditingPopupOpened = false;
      state.isNoteExportPopupOpened = false;
      state.isFileExplorerPopupOpened = false;
      state.isQrCodePopupOpened = false;
      state.isRefreshFilesList = false;
      state.isRefreshConsultationsList = false;
      state.isRefreshRecentNotesList = false;
      state.isRefreshTabs = false;
      state.isConsultationsDropdownOpened = false;
      state.selectedConsultation = null;
      state.selectedTemplate = null;
      state.templateOptionsXPosition = 0;
      state.templateOptionsYPosition = 0;
      state.isNoteTypePopupOpened = false;
      state.isFaxPopupOpened = false; // FIX: Add the new state to the reset function
    }
  },
});

export const {
  setConsultationId,
  setSection,
  setCategory,
  setPatientId,
  setPatientName,
  setSecurityPin,
  setCurrentTime,
  setLanguage,
  setNoteType,
  setNotesLength,
  setIsConsent,
  setPatientsList,
  setNotes,
  setSelectedConsultationNote,
  setSelectedAttachedFile,
  setSelectedImage,
  setAttachedFiles,
  setImages,
  setTemplates,
  setSavedNotes,
  setConsultationsList,
  setRecentNotes,
  setIsNewNoteCreated,
  setIsNewFileAttached,
  setIsNewImageUploaded,
  setIsNewTemplateCreated,
  setInitialRendering,
  setMouseInfo,
  setSelectPatientSearchQuery,
  setIsImageSlideOpened,
  setIsRefreshContent,
  setIsConsentPopupOpened,
  setIsLanguagePopupOpened,
  setIsNoteDeletionPopupOpened,
  setIsNoteViewerOpened,
  setIsSettingsPopupOpened,
  setIsNoteEditingPopupOpened,
  setIsNoteSettingsPopupOpened,
  setIsNoteExportPopupOpened,
  setIsFileExplorerPopupOpened,
  setIsQrCodePopupOpened,
  setIsRefreshFilesList,
  setIsRefreshConsultationsList,
  setIsRefreshRecentNotesList,
  setIsRefreshTabs,
  setIsConsultationsDropdownOpened,
  setSelectedConsultation,
  setSelectedTemplate,
  setTemplateOptionsXPosition,
  setTemplateOptionsYPosition,
  setIsNoteTypePopupOpened,
  setIsFaxPopupOpened,
  resetConsultation } = consultationSlice.actions;

export default consultationSlice.reducer;